import { ProductDetailDrawer } from "../ecommerce/product-detail-drawer/ProductDetailDrawer";
import type {
  DrawerImage,
  ProductDrawerCartConfig,
  ProductDrawerSpecification,
  ProductDrawerStockTabConfig,
  ProductDrawerTab,
  ProductDrawerViewModel,
} from "../ecommerce/product-detail-drawer/types";
import type { SpecificationItemDto } from "generated/models";
import { StackedDrawerShell } from "./drawer-stack";
import type { StackedDrawerProps } from "./drawer-stack";

export interface StackedProductDetailDrawerProps extends StackedDrawerProps {
  /** While true, shows a spinner in the product-details panel. */
  loading?: boolean;
  /** Accepted for API compatibility; the shared gallery handles its own image states. */
  isLoadingFallbackImages?: boolean;
  /** Null renders the unavailable/error state. */
  product: ProductDrawerViewModel | null;
  images?: DrawerImage[];
  /** Grouped specification feed for the searchable spec sections. */
  fullSpecification?: SpecificationItemDto[] | null;
  specification?: ProductDrawerSpecification | null;
  /** Optional "View Full Details" external link. */
  productDetailUrl?: string | null;
  /** Add-to-cart capability. Omit for read-only stacks. */
  cart?: ProductDrawerCartConfig;
  /** Stock / Inventory tab capability. Omit to render a single panel. */
  stockTab?: ProductDrawerStockTabConfig;
  initialTab?: ProductDrawerTab;
  onTabChange?: (tab: ProductDrawerTab) => void;
  /** Formats prices in the current storefront's currency; defaults to USD. */
  formatPrice?: (amount: number) => string;
  /** Telemetry event section. Defaults to `orders_stacked_quick_view`. */
  telemetrySection?: string;
}

/**
 * Stack-aware product detail drawer. Renders the shared {@link ProductDetailDrawer} inside a
 * {@link StackedDrawerShell} so it can open on top of another drawer (e.g. the Order Drawer) via
 * the DrawerStack system. Cart and stock-tab capabilities are opt-in via props.
 */
export function StackedProductDetailDrawer({
  open,
  onClose,
  onCloseAll,
  zIndex,
  depth,
  loading = false,
  product,
  images = [],
  fullSpecification,
  specification,
  productDetailUrl,
  cart,
  stockTab,
  initialTab,
  onTabChange,
  formatPrice,
  telemetrySection = "orders_stacked_quick_view",
}: StackedProductDetailDrawerProps) {
  return (
    <ProductDetailDrawer
      open={open}
      onClose={onClose}
      loading={loading}
      product={product}
      images={images}
      fullSpecification={fullSpecification}
      specificationFallback={specification}
      productDetailUrl={productDetailUrl}
      telemetrySection={telemetrySection}
      cart={cart}
      stockTab={stockTab}
      initialTab={initialTab}
      onTabChange={onTabChange}
      formatPrice={formatPrice}
      renderShell={(content) => (
        <StackedDrawerShell
          open={open}
          onClose={onClose}
          onCloseAll={onCloseAll}
          zIndex={zIndex}
          depth={depth}
          ariaLabel={`Product details - ${product?.productName ?? ""}`}
          paperSx={{ width: { xs: "100%", sm: 640 } }}
        >
          {content}
        </StackedDrawerShell>
      )}
    />
  );
}
